import { Emulator } from "./emulator.js";
import { createHistoryManager, type HistoryManager } from "./sprite-editor-history.js";
import { createWorkbenchMenuBar } from "./workbench-menu-bar.js";
import type { WorkbenchMenu, WorkbenchMenuBarController } from "./workbench-menu-types.js";
import { buttonMenuItem, checkboxMenuItem, menuSeparator, radioMenuItem } from "./workbench-menu-utils.js";
import {
  logicalColumns,
  renderSpriteCanvas,
  SPRITE_COUNT,
  SPRITE_HEIGHT,
  SPRITE_ROW_BYTES,
  SPRITE_SLOT_BYTES,
  SPRITE_WIDTH,
  type SpriteCanvasMarker
} from "./sprite-editor-render.js";
import {
  rasterizeFloodFill,
  rasterizeLine,
  rasterizeRectangleOutline,
  type PaintPoint
} from "./sprite-editor-paint-tools.js";

const SPRITE_PTR_OFFSET = 1000;
const SPRITE_MULTICOLOR_ADDR = 0xd01c;
const SPRITE_MC0_ADDR = 0xd025;
const SPRITE_MC1_ADDR = 0xd026;
const SPRITE_COLOR_BASE_ADDR = 0xd027;
const SESSION_STORAGE_KEY = "am8.sprite-editor.session.v1";

type ColorTarget = "sprite" | "mc0" | "mc1";
type BrushId = "transparent" | "sprite" | "mc0" | "mc1";
type SpriteTool = "brush" | "fill" | "line" | "rectangle";

type SpriteSlotState = {
  address: number;
  bytes: Uint8Array;
  color: number;
  index: number;
  multicolor: boolean;
  pointer: number;
};

type SpriteDraft = {
  activeBrush: BrushId;
  activeColorTarget: ColorTarget;
  bankBase: number;
  cursorCol: number;
  cursorRow: number;
  currentTool: SpriteTool;
  dirty: boolean;
  dirtySlots: boolean[];
  mc0: number;
  mc1: number;
  palette: Uint16Array;
  selectedIndex: number;
  sharedDirty: boolean;
  slots: SpriteSlotState[];
  spritePtrBase: number;
  spriteMaskDirty: boolean;
};

type SlotView = {
  address: HTMLSpanElement;
  dirtyBadge: HTMLSpanElement;
  button: HTMLButtonElement;
  colorSwatch: HTMLSpanElement;
  colorValue: HTMLSpanElement;
  mode: HTMLSpanElement;
  pointer: HTMLSpanElement;
  preview: HTMLCanvasElement;
  selectedBadge: HTMLSpanElement;
  title: HTMLSpanElement;
};

type ToggleView<T extends string> = {
  button: HTMLButtonElement;
  label: HTMLSpanElement;
  swatch: HTMLSpanElement;
  value: T;
};

type SlotClipboard = {
  bytes: Uint8Array;
  color: number;
  multicolor: boolean;
  sourceIndex: number;
};

type SpriteProjectSlotV1 = {
  address: number;
  bytes: number[];
  color: number;
  index: number;
  multicolor: boolean;
  pointer: number;
};

type SpriteProjectFileV1 = {
  activeBrush: BrushId;
  activeColorTarget: ColorTarget;
  bankBase: number;
  cursorCol: number;
  cursorRow: number;
  currentTool: SpriteTool;
  dirty: boolean;
  dirtySlots?: boolean[];
  format: "am8-sprite-project";
  mc0: number;
  mc1: number;
  palette: number[];
  selectedIndex: number;
  sharedDirty?: boolean;
  slots: SpriteProjectSlotV1[];
  spritePtrBase: number;
  spriteMaskDirty?: boolean;
  version: 1;
};

export type SpriteEditorElements = {
  addressValue: HTMLElement;
  brushes: HTMLDivElement;
  byteOutput: HTMLTextAreaElement;
  colorTargets: HTMLDivElement;
  draftIndicator: HTMLElement;
  editorCanvas: HTMLCanvasElement;
  exportProjectTrigger: HTMLButtonElement;
  exportSpriteTrigger: HTMLButtonElement;
  historyStatus: HTMLParagraphElement;
  importProjectInput: HTMLInputElement;
  importProjectTrigger: HTMLButtonElement;
  importSpriteInput: HTMLInputElement;
  importSpriteTrigger: HTMLButtonElement;
  modeSummary: HTMLParagraphElement;
  multicolorToggle: HTMLInputElement;
  newBlankTrigger: HTMLButtonElement;
  palette: HTMLDivElement;
  panel: HTMLElement;
  pointerValue: HTMLElement;
  readTrigger: HTMLButtonElement;
  redoTrigger: HTMLButtonElement;
  selectedSlotValue: HTMLElement;
  sharedHint: HTMLParagraphElement;
  slotActionButtons: HTMLDivElement;
  slotList: HTMLDivElement;
  spriteActionButtons: HTMLDivElement;
  status: HTMLParagraphElement;
  tools: HTMLDivElement;
  undoTrigger: HTMLButtonElement;
  writeSummary: HTMLParagraphElement;
  writeTrigger: HTMLButtonElement;
};

export type SpriteEditorController = {
  refresh(): void;
};

function formatHexByte(value: number): string {
  return (value & 0xff).toString(16).toUpperCase().padStart(2, "0");
}

function formatHexWord(value: number): string {
  return (value & 0xffff).toString(16).toUpperCase().padStart(4, "0");
}

function formatHexBytePrefixed(value: number): string {
  return `0x${formatHexByte(value)}`;
}

function formatHexWordPrefixed(value: number): string {
  return `0x${formatHexWord(value)}`;
}

function formatAsmByte(value: number): string {
  return `$${formatHexByte(value)}`;
}

function rgb444ToCss(value: number): string {
  const r = ((value >> 8) & 0x0f) * 17;
  const g = ((value >> 4) & 0x0f) * 17;
  const b = (value & 0x0f) * 17;
  return `rgb(${r}, ${g}, ${b})`;
}

function colorLabel(target: ColorTarget): string {
  if (target === "sprite") {
    return "Sprite Color";
  }
  if (target === "mc0") {
    return "MC0";
  }
  return "MC1";
}

function brushLabel(brush: BrushId): string {
  if (brush === "transparent") {
    return "Transparent";
  }
  if (brush === "sprite") {
    return "Sprite";
  }
  if (brush === "mc0") {
    return "MC0";
  }
  return "MC1";
}

function toolLabel(tool: SpriteTool): string {
  if (tool === "fill") {
    return "Fill";
  }
  if (tool === "line") {
    return "Line";
  }
  if (tool === "rectangle") {
    return "Rectangle";
  }
  return "Brush";
}

function cloneBytes(data: Uint8Array): Uint8Array {
  const copy = new Uint8Array(data.byteLength);
  copy.set(data);
  return copy;
}

function clonePalette(data: Uint16Array): Uint16Array {
  const copy = new Uint16Array(data.length);
  copy.set(data);
  return copy;
}

function cloneSlot(slot: SpriteSlotState): SpriteSlotState {
  return {
    address: slot.address,
    bytes: cloneBytes(slot.bytes),
    color: slot.color,
    index: slot.index,
    multicolor: slot.multicolor,
    pointer: slot.pointer
  };
}

function cloneDraft(draft: SpriteDraft): SpriteDraft {
  return {
    activeBrush: draft.activeBrush,
    activeColorTarget: draft.activeColorTarget,
    bankBase: draft.bankBase,
    cursorCol: draft.cursorCol,
    cursorRow: draft.cursorRow,
    currentTool: draft.currentTool,
    dirty: draft.dirty,
    dirtySlots: [...draft.dirtySlots],
    mc0: draft.mc0,
    mc1: draft.mc1,
    palette: clonePalette(draft.palette),
    selectedIndex: draft.selectedIndex,
    sharedDirty: draft.sharedDirty,
    slots: draft.slots.map(cloneSlot),
    spritePtrBase: draft.spritePtrBase,
    spriteMaskDirty: draft.spriteMaskDirty
  };
}

function recomputeDirty(draft: SpriteDraft): void {
  draft.dirty = draft.sharedDirty || draft.spriteMaskDirty || draft.dirtySlots.some(Boolean);
}

function clearDirtyState(draft: SpriteDraft): void {
  draft.dirtySlots = Array.from({ length: SPRITE_COUNT }, () => false);
  draft.sharedDirty = false;
  draft.spriteMaskDirty = false;
  recomputeDirty(draft);
}

function markSelectedSlotDirty(draft: SpriteDraft): void {
  draft.dirtySlots[draft.selectedIndex] = true;
  recomputeDirty(draft);
}

function markAllSlotsDirty(draft: SpriteDraft): void {
  draft.dirtySlots = Array.from({ length: SPRITE_COUNT }, () => true);
  recomputeDirty(draft);
}

function bankBaseForVideoCtrl(videoCtrl: number): number {
  return (videoCtrl & 0x01) === 0 ? 0x0000 : 0x8000;
}

function screenBaseForMemPtr(bankBase: number, memPtr: number): number {
  return bankBase + (((memPtr >> 4) & 0x0f) << 10);
}

function buildByteOutput(bytes: Uint8Array): string {
  const lines: string[] = [];
  for (let row = 0; row < SPRITE_HEIGHT; row += 1) {
    const base = row * SPRITE_ROW_BYTES;
    lines.push(
      `.byte ${formatAsmByte(bytes[base])}, ${formatAsmByte(bytes[base + 1])}, ${formatAsmByte(bytes[base + 2])}`
    );
  }
  lines.push(`.byte ${formatAsmByte(bytes[SPRITE_SLOT_BYTES - 1])}`);
  return lines.join("\n");
}

function brushToCode(brush: BrushId): number {
  if (brush === "transparent") {
    return 0;
  }
  if (brush === "mc0") {
    return 1;
  }
  if (brush === "sprite") {
    return 2;
  }
  return 3;
}

function readMonoCellCode(slot: SpriteSlotState, row: number, col: number): number {
  const byteIndex = row * SPRITE_ROW_BYTES + (col >> 3);
  const mask = 0x80 >> (col & 0x07);
  return (slot.bytes[byteIndex] & mask) !== 0 ? 2 : 0;
}

function readMulticolorCellCode(slot: SpriteSlotState, row: number, col: number): number {
  const byteIndex = row * SPRITE_ROW_BYTES + (col >> 2);
  const shift = 6 - ((col & 0x03) * 2);
  return (slot.bytes[byteIndex] >> shift) & 0x03;
}

function readCellCode(slot: SpriteSlotState, row: number, col: number): number {
  return slot.multicolor ? readMulticolorCellCode(slot, row, col) : readMonoCellCode(slot, row, col);
}

function cellValue(slot: SpriteSlotState, row: number, col: number): BrushId {
  const code = readCellCode(slot, row, col);
  if (code === 1) {
    return "mc0";
  }
  if (code === 2) {
    return "sprite";
  }
  if (code === 3) {
    return "mc1";
  }
  return "transparent";
}

function setMonoCellCode(slot: SpriteSlotState, row: number, col: number, code: number): void {
  const byteIndex = row * SPRITE_ROW_BYTES + (col >> 3);
  const mask = 0x80 >> (col & 0x07);
  if ((code & 0x03) === 0) {
    slot.bytes[byteIndex] &= ~mask;
    return;
  }
  slot.bytes[byteIndex] |= mask;
}

function setMulticolorCellCode(slot: SpriteSlotState, row: number, col: number, code: number): void {
  const byteIndex = row * SPRITE_ROW_BYTES + (col >> 2);
  const shift = 6 - ((col & 0x03) * 2);
  const cleared = slot.bytes[byteIndex] & ~(0x03 << shift);
  slot.bytes[byteIndex] = cleared | ((code & 0x03) << shift);
}

function setCellCode(slot: SpriteSlotState, row: number, col: number, code: number): void {
  if (slot.multicolor) {
    setMulticolorCellCode(slot, row, col, code);
    return;
  }
  setMonoCellCode(slot, row, col, code === 0 ? 0 : 2);
}

function applyBrush(slot: SpriteSlotState, row: number, col: number, brush: BrushId, mode: "set" | "toggle"): boolean {
  const currentCode = readCellCode(slot, row, col);
  const desiredCode = slot.multicolor ? brushToCode(brush) : brush === "transparent" ? 0 : 2;
  const nextCode = mode === "toggle" && currentCode === desiredCode ? 0 : desiredCode;
  if (currentCode === nextCode) {
    return false;
  }
  setCellCode(slot, row, col, nextCode);
  return true;
}

function slotCodes(slot: SpriteSlotState): Uint8Array {
  const cols = logicalColumns(slot.multicolor);
  const codes = new Uint8Array(cols * SPRITE_HEIGHT);
  for (let row = 0; row < SPRITE_HEIGHT; row += 1) {
    for (let col = 0; col < cols; col += 1) {
      codes[row * cols + col] = readCellCode(slot, row, col);
    }
  }
  return codes;
}

function writeSlotCodes(slot: SpriteSlotState, codes: Uint8Array): void {
  const cols = logicalColumns(slot.multicolor);
  slot.bytes.fill(0);
  for (let row = 0; row < SPRITE_HEIGHT; row += 1) {
    for (let col = 0; col < cols; col += 1) {
      const code = codes[row * cols + col] ?? 0;
      if (code !== 0) {
        setCellCode(slot, row, col, code);
      }
    }
  }
}

function mirrorSlotHorizontal(slot: SpriteSlotState): void {
  const cols = logicalColumns(slot.multicolor);
  const source = slotCodes(slot);
  const target = new Uint8Array(source.length);
  for (let row = 0; row < SPRITE_HEIGHT; row += 1) {
    for (let col = 0; col < cols; col += 1) {
      target[row * cols + (cols - 1 - col)] = source[row * cols + col] ?? 0;
    }
  }
  writeSlotCodes(slot, target);
}

function mirrorSlotVertical(slot: SpriteSlotState): void {
  const cols = logicalColumns(slot.multicolor);
  const source = slotCodes(slot);
  const target = new Uint8Array(source.length);
  for (let row = 0; row < SPRITE_HEIGHT; row += 1) {
    for (let col = 0; col < cols; col += 1) {
      target[(SPRITE_HEIGHT - 1 - row) * cols + col] = source[row * cols + col] ?? 0;
    }
  }
  writeSlotCodes(slot, target);
}

function shiftSlot(slot: SpriteSlotState, deltaCol: number, deltaRow: number): void {
  const cols = logicalColumns(slot.multicolor);
  const source = slotCodes(slot);
  const target = new Uint8Array(source.length);
  for (let row = 0; row < SPRITE_HEIGHT; row += 1) {
    for (let col = 0; col < cols; col += 1) {
      const sourceCol = col - deltaCol;
      const sourceRow = row - deltaRow;
      if (sourceCol < 0 || sourceCol >= cols || sourceRow < 0 || sourceRow >= SPRITE_HEIGHT) {
        continue;
      }
      target[row * cols + col] = source[sourceRow * cols + sourceCol] ?? 0;
    }
  }
  writeSlotCodes(slot, target);
}

function multicolorMask(slots: SpriteSlotState[]): number {
  return slots.reduce((mask, slot) => mask | (slot.multicolor ? 1 << slot.index : 0), 0) & 0xff;
}

function readDraftFromEmulator(emulator: Emulator, previous: SpriteDraft | null): SpriteDraft {
  const voc = emulator.getVocState();
  const ioc = emulator.getIocState();
  const bankBase = bankBaseForVideoCtrl(ioc.videoCtrl);
  const spritePtrBase = screenBaseForMemPtr(bankBase, voc.memPtr) + SPRITE_PTR_OFFSET;
  const slots: SpriteSlotState[] = [];

  for (let index = 0; index < SPRITE_COUNT; index += 1) {
    const pointer = voc.spritePtr[index];
    const address = bankBase + (pointer << 6);
    slots.push({
      address,
      bytes: emulator.copyMemory(address, SPRITE_SLOT_BYTES),
      color: voc.spriteColor[index] & 0x0f,
      index,
      multicolor: (voc.spriteMulticolor & (1 << index)) !== 0,
      pointer
    });
  }

  const next: SpriteDraft = {
    activeBrush: previous?.activeBrush ?? "sprite",
    activeColorTarget: previous?.activeColorTarget ?? "sprite",
    bankBase,
    cursorCol: previous?.cursorCol ?? 0,
    cursorRow: previous?.cursorRow ?? 0,
    currentTool: previous?.currentTool ?? "brush",
    dirty: false,
    dirtySlots: Array.from({ length: SPRITE_COUNT }, () => false),
    mc0: voc.spriteMc0 & 0x0f,
    mc1: voc.spriteMc1 & 0x0f,
    palette: clonePalette(voc.palette),
    selectedIndex: Math.max(0, Math.min(SPRITE_COUNT - 1, previous?.selectedIndex ?? 0)),
    sharedDirty: false,
    slots,
    spritePtrBase,
    spriteMaskDirty: false
  };
  normalizeCursor(next);
  return next;
}

function createProjectData(draft: SpriteDraft): SpriteProjectFileV1 {
  return {
    activeBrush: draft.activeBrush,
    activeColorTarget: draft.activeColorTarget,
    bankBase: draft.bankBase,
    cursorCol: draft.cursorCol,
    cursorRow: draft.cursorRow,
    currentTool: draft.currentTool,
    dirty: draft.dirty,
    dirtySlots: [...draft.dirtySlots],
    format: "am8-sprite-project",
    mc0: draft.mc0,
    mc1: draft.mc1,
    palette: Array.from(draft.palette, (value) => value & 0x0fff),
    selectedIndex: draft.selectedIndex,
    sharedDirty: draft.sharedDirty,
    slots: draft.slots.map((slot) => ({
      address: slot.address,
      bytes: Array.from(slot.bytes),
      color: slot.color,
      index: slot.index,
      multicolor: slot.multicolor,
      pointer: slot.pointer
    })),
    spritePtrBase: draft.spritePtrBase,
    spriteMaskDirty: draft.spriteMaskDirty,
    version: 1
  };
}

function parseProjectData(text: string): SpriteDraft {
  const raw = JSON.parse(text) as Partial<SpriteProjectFileV1>;
  if (raw.format !== "am8-sprite-project" || raw.version !== 1) {
    throw new Error("Sprite project must use format am8-sprite-project version 1.");
  }
  if (!Array.isArray(raw.palette) || raw.palette.length !== 16) {
    throw new Error("Sprite project must contain exactly 16 palette entries.");
  }
  if (!Array.isArray(raw.slots) || raw.slots.length !== SPRITE_COUNT) {
    throw new Error(`Sprite project must contain exactly ${SPRITE_COUNT} slots.`);
  }

  const slots = raw.slots.map((slot, index) => {
    if (!Array.isArray(slot.bytes) || slot.bytes.length !== SPRITE_SLOT_BYTES) {
      throw new Error(`Sprite project slot ${index} must contain exactly ${SPRITE_SLOT_BYTES} bytes.`);
    }
    return {
      address: Number(slot.address) & 0xffff,
      bytes: Uint8Array.from(slot.bytes, (value) => value & 0xff),
      color: Number(slot.color) & 0x0f,
      index,
      multicolor: Boolean(slot.multicolor),
      pointer: Number(slot.pointer) & 0xff
    };
  });

  const draft: SpriteDraft = {
    activeBrush: raw.activeBrush === "transparent" || raw.activeBrush === "sprite" || raw.activeBrush === "mc0" || raw.activeBrush === "mc1"
      ? raw.activeBrush
      : "sprite",
    activeColorTarget: raw.activeColorTarget === "mc0" || raw.activeColorTarget === "mc1" ? raw.activeColorTarget : "sprite",
    bankBase: Number(raw.bankBase) & 0xffff,
    cursorCol: Number(raw.cursorCol) || 0,
    cursorRow: Number(raw.cursorRow) || 0,
    currentTool: raw.currentTool === "fill" || raw.currentTool === "line" || raw.currentTool === "rectangle" ? raw.currentTool : "brush",
    dirty: Boolean(raw.dirty),
    dirtySlots: Array.isArray(raw.dirtySlots) && raw.dirtySlots.length === SPRITE_COUNT
      ? raw.dirtySlots.map(Boolean)
      : Array.from({ length: SPRITE_COUNT }, () => Boolean(raw.dirty)),
    mc0: Number(raw.mc0) & 0x0f,
    mc1: Number(raw.mc1) & 0x0f,
    palette: Uint16Array.from(raw.palette, (value) => value & 0x0fff),
    selectedIndex: Math.max(0, Math.min(SPRITE_COUNT - 1, Number(raw.selectedIndex) || 0)),
    sharedDirty: Boolean(raw.sharedDirty ?? raw.dirty),
    slots,
    spritePtrBase: Number(raw.spritePtrBase) & 0xffff,
    spriteMaskDirty: Boolean(raw.spriteMaskDirty ?? raw.dirty)
  };
  recomputeDirty(draft);
  normalizeCursor(draft);
  return draft;
}

function saveDraftToSession(draft: SpriteDraft | null): void {
  try {
    if (draft === null) {
      window.sessionStorage.removeItem(SESSION_STORAGE_KEY);
      return;
    }
    window.sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(createProjectData(draft)));
  } catch {
    // Ignore sessionStorage failures.
  }
}

function restoreDraftFromSession(): SpriteDraft | null {
  try {
    const raw = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (raw === null) {
      return null;
    }
    return parseProjectData(raw);
  } catch {
    return null;
  }
}

function downloadBinary(bytes: Uint8Array, fileName: string): void {
  const blobBytes = new Uint8Array(bytes.byteLength);
  blobBytes.set(bytes);
  const blob = new Blob([blobBytes.buffer], { type: "application/octet-stream" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.hidden = true;
  document.body.append(link);
  link.click();
  link.remove();
  setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 0);
}

function downloadText(fileName: string, text: string): void {
  const blob = new Blob([text], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.hidden = true;
  document.body.append(link);
  link.click();
  link.remove();
  setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 0);
}

function isEditableElement(target: EventTarget | null): boolean {
  return target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement;
}

function normalizeCursor(draft: SpriteDraft): void {
  const slot = draft.slots[draft.selectedIndex];
  if (slot === undefined) {
    draft.selectedIndex = 0;
    draft.cursorCol = 0;
    draft.cursorRow = 0;
    return;
  }
  draft.cursorRow = Math.max(0, Math.min(SPRITE_HEIGHT - 1, draft.cursorRow));
  draft.cursorCol = Math.max(0, Math.min(logicalColumns(slot.multicolor) - 1, draft.cursorCol));
  if (!slot.multicolor && (draft.activeBrush === "mc0" || draft.activeBrush === "mc1")) {
    draft.activeBrush = "sprite";
  }
}

function createSlotView(index: number): SlotView {
  const button = document.createElement("button");
  button.type = "button";
  button.className = "sprite-slot";

  const preview = document.createElement("canvas");
  preview.width = SPRITE_WIDTH * 4;
  preview.height = SPRITE_HEIGHT * 4;
  preview.className = "sprite-slot__preview";
  button.append(preview);

  const meta = document.createElement("div");
  meta.className = "sprite-slot__meta";
  button.append(meta);

  const heading = document.createElement("div");
  heading.className = "sprite-slot__heading";
  meta.append(heading);

  const title = document.createElement("span");
  title.className = "sprite-slot__title";
  title.textContent = `Sprite ${index}`;
  heading.append(title);

  const badges = document.createElement("span");
  badges.className = "sprite-slot__badges";
  heading.append(badges);

  const selectedBadge = document.createElement("span");
  selectedBadge.className = "sprite-slot__badge sprite-slot__badge--selected";
  selectedBadge.textContent = "Selected";
  selectedBadge.hidden = true;
  badges.append(selectedBadge);

  const dirtyBadge = document.createElement("span");
  dirtyBadge.className = "sprite-slot__badge sprite-slot__badge--dirty";
  dirtyBadge.textContent = "Dirty";
  dirtyBadge.hidden = true;
  badges.append(dirtyBadge);

  const pointer = document.createElement("span");
  pointer.className = "sprite-slot__detail";
  meta.append(pointer);

  const address = document.createElement("span");
  address.className = "sprite-slot__detail";
  meta.append(address);

  const footer = document.createElement("div");
  footer.className = "sprite-slot__footer";
  meta.append(footer);

  const mode = document.createElement("span");
  mode.className = "sprite-slot__mode";
  footer.append(mode);

  const colorWrap = document.createElement("span");
  colorWrap.className = "sprite-slot__color";
  footer.append(colorWrap);

  const colorSwatch = document.createElement("span");
  colorSwatch.className = "sprite-slot__swatch";
  colorWrap.append(colorSwatch);

  const colorValue = document.createElement("span");
  colorWrap.append(colorValue);

  return { address, dirtyBadge, button, colorSwatch, colorValue, mode, pointer, preview, selectedBadge, title };
}

function createToggleView<T extends string>(labelText: string, value: T): ToggleView<T> {
  const button = document.createElement("button");
  button.type = "button";
  button.className = "sprite-chip";

  const swatch = document.createElement("span");
  swatch.className = "sprite-chip__swatch";
  button.append(swatch);

  const label = document.createElement("span");
  label.textContent = labelText;
  button.append(label);

  return { button, label, swatch, value };
}

function canvasCellFromEvent(event: MouseEvent | PointerEvent, canvas: HTMLCanvasElement, slot: SpriteSlotState): PaintPoint | null {
  const rect = canvas.getBoundingClientRect();
  const x = ((event.clientX - rect.left) * canvas.width) / rect.width;
  const y = ((event.clientY - rect.top) * canvas.height) / rect.height;
  if (x < 0 || y < 0 || x >= canvas.width || y >= canvas.height) {
    return null;
  }
  const cols = logicalColumns(slot.multicolor);
  return {
    x: Math.max(0, Math.min(cols - 1, Math.floor((x / canvas.width) * cols))),
    y: Math.max(0, Math.min(SPRITE_HEIGHT - 1, Math.floor((y / canvas.height) * SPRITE_HEIGHT)))
  };
}

export function setupSpriteEditor(emulator: Emulator, elements: SpriteEditorElements): SpriteEditorController {
  const menuHost = document.getElementById("sprite-menu-bar");
  if (!(menuHost instanceof HTMLDivElement)) {
    throw new Error("missing sprite menu bar host");
  }
  const slotViews = Array.from({ length: SPRITE_COUNT }, (_, index) => createSlotView(index));
  const colorTargetViews = [
    createToggleView("Sprite Color", "sprite" as const),
    createToggleView("MC0", "mc0" as const),
    createToggleView("MC1", "mc1" as const)
  ];
  const brushViews = [
    createToggleView("Transparent", "transparent" as const),
    createToggleView("Sprite", "sprite" as const),
    createToggleView("MC0", "mc0" as const),
    createToggleView("MC1", "mc1" as const)
  ];
  const paletteButtons = Array.from({ length: 16 }, (_, index) => {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "sprite-palette__button";
    button.textContent = index.toString(16).toUpperCase();
    return button;
  });
  const history: HistoryManager<SpriteDraft> = createHistoryManager(cloneDraft, 64);

  let draft: SpriteDraft | null = restoreDraftFromSession();
  let clipboard: SlotClipboard | null = null;
  let pendingShapeAnchor: PaintPoint | null = null;
  let paintingPointerId: number | null = null;
  let paintingBrush: BrushId = "sprite";
  let strokeHistorySnapshot: SpriteDraft | null = null;
  let strokeMutated = false;
  let lastPaintedPoint: PaintPoint | null = null;
  let menuBar: WorkbenchMenuBarController | null = null;

  elements.slotList.replaceChildren(...slotViews.map((view) => view.button));
  elements.colorTargets.replaceChildren(...colorTargetViews.map((view) => view.button));
  elements.brushes.replaceChildren(...brushViews.map((view) => view.button));
  elements.palette.replaceChildren(...paletteButtons);
  elements.editorCanvas.tabIndex = 0;
  elements.byteOutput.value = "";
  elements.multicolorToggle.checked = false;
  elements.multicolorToggle.disabled = true;

  const setStatus = (message: string): void => {
    elements.status.textContent = message;
  };

  const syncSession = (): void => {
    saveDraftToSession(draft);
  };

  const refreshMenuBar = (): void => {
    menuBar?.refresh();
  };

  const draftOrThrow = (): SpriteDraft => {
    if (draft === null) {
      throw new Error("Read, import, or restore a sprite draft first.");
    }
    return draft;
  };

  const selectedSlot = (): SpriteSlotState | null => {
    if (draft === null) {
      return null;
    }
    return draft.slots[draft.selectedIndex] ?? null;
  };

  const commitStrokeHistory = (): void => {
    if (strokeHistorySnapshot !== null && strokeMutated) {
      history.pushUndoSnapshot("Paint sprite cells", strokeHistorySnapshot);
    }
    strokeHistorySnapshot = null;
    strokeMutated = false;
    lastPaintedPoint = null;
    paintingPointerId = null;
    renderHistoryControls();
  };

  const clearTransientToolState = (): void => {
    pendingShapeAnchor = null;
    commitStrokeHistory();
  };

  const confirmDiscardDirtyDraft = (action: string): boolean => {
    if (draft === null || !draft.dirty) {
      return true;
    }
    return window.confirm(`Discard unsaved sprite edits before ${action}?`);
  };

  const renderDirtyIndicator = (): void => {
    if (draft === null) {
      elements.draftIndicator.textContent = "No Draft";
      elements.draftIndicator.classList.remove("is-dirty");
      elements.draftIndicator.classList.remove("is-synced");
      return;
    }
    const slotCount = draft.dirtySlots.filter(Boolean).length;
    const hasShared = draft.sharedDirty || draft.spriteMaskDirty;
    elements.draftIndicator.textContent = draft.dirty
      ? `Unsaved${slotCount > 0 || hasShared ? `: ${slotCount > 0 ? `${slotCount} slot${slotCount === 1 ? "" : "s"}` : "shared only"}${slotCount > 0 && hasShared ? " + shared" : ""}` : ""}`
      : "Synced to Emulator";
    elements.draftIndicator.classList.toggle("is-dirty", draft.dirty);
    elements.draftIndicator.classList.toggle("is-synced", !draft.dirty);
  };

  function renderHistoryControls(): void {
    const state = history.getState();
    elements.undoTrigger.disabled = !state.canUndo;
    elements.redoTrigger.disabled = !state.canRedo;
    elements.undoTrigger.title = state.lastUndoLabel === null ? "Undo" : `Undo ${state.lastUndoLabel}`;
    elements.redoTrigger.title = state.lastRedoLabel === null ? "Redo" : `Redo ${state.lastRedoLabel}`;
    elements.historyStatus.textContent = `Undo ${state.undoDepth}${state.lastUndoLabel !== null ? ` (${state.lastUndoLabel})` : ""} | Redo ${state.redoDepth}${state.lastRedoLabel !== null ? ` (${state.lastRedoLabel})` : ""}`;
  }

  const renderSlotList = (updatedIndices?: number[]): void => {
    const onlyUpdated = updatedIndices === undefined ? null : new Set(updatedIndices);
    for (const view of slotViews) {
      if (draft === null) {
        view.button.classList.remove("is-active");
        view.button.classList.remove("is-dirty");
        view.button.disabled = true;
        view.button.setAttribute("aria-label", `Sprite ${slotViews.indexOf(view)} unavailable until sprites are loaded.`);
        view.title.textContent = `Sprite ${slotViews.indexOf(view)}`;
        view.selectedBadge.hidden = true;
        view.dirtyBadge.hidden = true;
        view.pointer.textContent = "PTR -";
        view.address.textContent = "0x0000";
        view.mode.textContent = "-";
        view.colorValue.textContent = "P--";
        view.colorSwatch.style.backgroundColor = "rgba(255, 255, 255, 0.08)";
        const context = view.preview.getContext("2d");
        if (context !== null) {
          context.clearRect(0, 0, view.preview.width, view.preview.height);
        }
        continue;
      }

      const slot = draft.slots[slotViews.indexOf(view)]!;
      view.button.disabled = false;
      view.button.classList.toggle("is-active", slot.index === draft.selectedIndex);
      view.button.classList.toggle("is-dirty", draft.dirtySlots[slot.index] ?? false);
      view.title.textContent = `Sprite ${slot.index}`;
      view.selectedBadge.hidden = slot.index !== draft.selectedIndex;
      view.dirtyBadge.hidden = !(draft.dirtySlots[slot.index] ?? false);
      view.pointer.textContent = `PTR ${formatHexBytePrefixed(slot.pointer)}`;
      view.address.textContent = formatHexWordPrefixed(slot.address);
      view.mode.textContent = slot.multicolor ? "multicolor" : "mono";
      view.colorValue.textContent = `P${formatHexByte(slot.color)}`;
      view.colorSwatch.style.backgroundColor = rgb444ToCss(draft.palette[slot.color]);
      view.button.setAttribute(
        "aria-label",
        `Sprite ${slot.index}, pointer ${formatHexBytePrefixed(slot.pointer)}, address ${formatHexWordPrefixed(slot.address)}, ${slot.multicolor ? "multicolor" : "single-color"}${draft.dirtySlots[slot.index] ? ", unsaved local edits" : ""}.`
      );
      if (onlyUpdated !== null && !onlyUpdated.has(slot.index)) {
        continue;
      }
      renderSpriteCanvas(view.preview, slot, draft.palette, draft.mc0, draft.mc1, { showGrid: false });
    }
  };

  const renderEditor = (): void => {
    const slot = selectedSlot();
    if (draft === null || slot === null) {
      elements.selectedSlotValue.textContent = "-";
      elements.pointerValue.textContent = "0x0000";
      elements.addressValue.textContent = "0x0000";
      elements.modeSummary.textContent = "Read sprites, import a project, or restore a browser draft to inspect and edit sprite slots.";
      elements.writeSummary.textContent = "Read sprites to inspect current write targets. All editing stays local until you choose Write Sprite + Registers.";
      elements.writeSummary.title = elements.writeSummary.textContent;
      elements.sharedHint.textContent = "MC0 and MC1 are shared across all multicolor sprites.";
      elements.byteOutput.value = "";
      elements.multicolorToggle.checked = false;
      elements.multicolorToggle.disabled = true;
      const editorContext = elements.editorCanvas.getContext("2d");
      if (editorContext !== null) {
        editorContext.clearRect(0, 0, elements.editorCanvas.width, elements.editorCanvas.height);
        editorContext.fillStyle = "rgba(229, 239, 232, 0.72)";
        editorContext.font = "14px Iosevka, monospace";
        editorContext.fillText("Load or restore a sprite draft to edit it.", 16, 28);
      }
      return;
    }

    normalizeCursor(draft);
    elements.selectedSlotValue.textContent = `S${slot.index}`;
    elements.pointerValue.textContent = formatHexBytePrefixed(slot.pointer);
    elements.addressValue.textContent = formatHexWordPrefixed(slot.address);
    elements.multicolorToggle.disabled = false;
    elements.multicolorToggle.checked = slot.multicolor;
    elements.modeSummary.textContent = slot.multicolor
      ? "Multicolor uses 12 wide cells per row: transparent, MC0, sprite color, and MC1. Drag paints without toggling, while single clicks still let you toggle the current brush back to transparent."
      : "Single-color uses 24 one-bit pixels per row. Drag paints solid pixels, while a single click on an already painted cell toggles it back to transparent.";
    elements.writeSummary.textContent = `Write S${slot.index} -> ${formatHexWordPrefixed(slot.address)} · Color P${formatHexByte(slot.color)} · MC0 P${formatHexByte(draft.mc0)} · MC1 P${formatHexByte(draft.mc1)} · Mask ${formatHexBytePrefixed(multicolorMask(draft.slots))}`;
    elements.writeSummary.title = `Write Sprite + Registers stores Sprite ${slot.index} at ${formatHexWordPrefixed(slot.address)} and updates Sprite Color P${formatHexByte(slot.color)}, MC0 P${formatHexByte(draft.mc0)}, MC1 P${formatHexByte(draft.mc1)}, and SPR_MC_EN ${formatHexBytePrefixed(multicolorMask(draft.slots))}.`;
    elements.sharedHint.textContent = slot.multicolor
      ? "MC0 and MC1 affect every multicolor sprite. Changing them here updates all multicolor previews when you write the draft back."
      : "MC0 and MC1 remain shared registers even while this slot is currently single-color.";
    elements.byteOutput.value = buildByteOutput(slot.bytes);

    const cursor: SpriteCanvasMarker = { col: draft.cursorCol, row: draft.cursorRow };
    const anchor = pendingShapeAnchor === null ? null : { col: pendingShapeAnchor.x, row: pendingShapeAnchor.y, dashed: true };
    renderSpriteCanvas(elements.editorCanvas, slot, draft.palette, draft.mc0, draft.mc1, {
      anchor,
      cursor,
      showGrid: true
    });
  };

  const renderColorTargets = (): void => {
    const slot = selectedSlot();
    for (const view of colorTargetViews) {
      const enabled = draft !== null && slot !== null;
      view.button.disabled = !enabled;
      if (!enabled || draft === null || slot === null) {
        view.button.classList.remove("is-active");
        view.button.setAttribute("aria-pressed", "false");
        view.label.textContent = colorLabel(view.value);
        view.swatch.style.backgroundColor = "rgba(255, 255, 255, 0.14)";
        continue;
      }
      const paletteIndex = view.value === "sprite" ? slot.color : view.value === "mc0" ? draft.mc0 : draft.mc1;
      const active = view.value === draft.activeColorTarget;
      view.button.classList.toggle("is-active", active);
      view.button.setAttribute("aria-pressed", active ? "true" : "false");
      view.button.setAttribute("aria-label", `${colorLabel(view.value)} target palette ${paletteIndex}`);
      view.label.textContent = `${colorLabel(view.value)} P${formatHexByte(paletteIndex)}`;
      view.swatch.style.backgroundColor = rgb444ToCss(draft.palette[paletteIndex]);
    }
  };

  const renderBrushes = (): void => {
    const slot = selectedSlot();
    for (const view of brushViews) {
      const enabled = draft !== null && slot !== null;
      const disabled = !enabled || (!slot.multicolor && (view.value === "mc0" || view.value === "mc1"));
      view.button.disabled = disabled;
      if (!enabled || draft === null || slot === null) {
        view.button.classList.remove("is-active");
        view.button.setAttribute("aria-pressed", "false");
        view.label.textContent = brushLabel(view.value);
        view.swatch.style.background = "rgba(255, 255, 255, 0.14)";
        continue;
      }
      const active = view.value === draft.activeBrush;
      view.button.classList.toggle("is-active", active);
      view.button.setAttribute("aria-pressed", active ? "true" : "false");
      view.button.setAttribute("aria-label", `${brushLabel(view.value)} brush`);
      view.label.textContent = brushLabel(view.value);
      if (view.value === "transparent") {
        view.swatch.style.background = "linear-gradient(135deg, rgba(255, 255, 255, 0.06), rgba(255, 255, 255, 0.16))";
      } else if (view.value === "sprite") {
        view.swatch.style.background = rgb444ToCss(draft.palette[slot.color]);
      } else if (view.value === "mc0") {
        view.swatch.style.background = rgb444ToCss(draft.palette[draft.mc0]);
      } else {
        view.swatch.style.background = rgb444ToCss(draft.palette[draft.mc1]);
      }
    }
  };

  const renderPalette = (): void => {
    const slot = selectedSlot();
    if (draft === null || slot === null) {
      for (const button of paletteButtons) {
        button.disabled = true;
        button.classList.remove("is-active");
      }
      return;
    }
    const currentPaletteIndex =
      draft.activeColorTarget === "sprite"
        ? slot.color
        : draft.activeColorTarget === "mc0"
          ? draft.mc0
          : draft.mc1;
    for (let index = 0; index < paletteButtons.length; index += 1) {
      const button = paletteButtons[index];
      const color = rgb444ToCss(draft.palette[index]);
      const rgb = draft.palette[index];
      const brightness = ((rgb >> 8) & 0x0f) + ((rgb >> 4) & 0x0f) + (rgb & 0x0f);
      button.disabled = false;
      button.style.backgroundColor = color;
      button.style.color = brightness >= 20 ? "#09141c" : "#e5efe8";
      button.classList.toggle("is-active", index === currentPaletteIndex);
      button.setAttribute("aria-label", `Palette ${index}`);
      button.setAttribute("aria-pressed", index === currentPaletteIndex ? "true" : "false");
    }
  };

  const renderToolButtons = (): void => {
    elements.tools.replaceChildren();
    for (const tool of ["brush", "fill", "line", "rectangle"] as const) {
      const button = document.createElement("button");
      button.type = "button";
      button.className = "sprite-tool-button";
      button.textContent = toolLabel(tool);
      button.disabled = draft === null;
      button.classList.toggle("is-active", draft?.currentTool === tool);
      button.setAttribute("aria-pressed", draft?.currentTool === tool ? "true" : "false");
      button.addEventListener("click", () => {
        selectTool(tool);
      });
      elements.tools.append(button);
    }
  };

  function selectTool(tool: SpriteTool): void {
    const currentDraft = draftOrThrow();
    currentDraft.currentTool = tool;
    pendingShapeAnchor = null;
    syncSession();
    renderToolButtons();
    renderEditor();
    refreshMenuBar();
    setStatus(`Selected ${toolLabel(tool)} tool.`);
  }

  const buildMenus = (): WorkbenchMenu[] => [
    {
      id: "file",
      label: "File",
      items: [
        buttonMenuItem("read-sprites", elements.readTrigger),
        buttonMenuItem("new-blank", elements.newBlankTrigger),
        menuSeparator(),
        buttonMenuItem("import-sprite", elements.importSpriteTrigger),
        buttonMenuItem("export-sprite", elements.exportSpriteTrigger),
        menuSeparator(),
        buttonMenuItem("import-project", elements.importProjectTrigger),
        buttonMenuItem("export-project", elements.exportProjectTrigger)
      ]
    },
    {
      id: "edit",
      label: "Edit",
      items: [
        buttonMenuItem("undo", elements.undoTrigger, { shortcut: "Ctrl/Cmd+Z" }),
        buttonMenuItem("redo", elements.redoTrigger, { shortcut: "Ctrl/Cmd+Shift+Z" })
      ]
    },
    {
      id: "view",
      label: "View",
      items: [
        checkboxMenuItem("multicolor", "Multicolor", elements.multicolorToggle),
        {
          type: "action",
          id: "focus-editor",
          label: "Focus Editor Canvas",
          enabled: true,
          run() {
            elements.editorCanvas.focus();
          }
        }
      ]
    },
    {
      id: "tools",
      label: "Tools",
      items: [
        radioMenuItem("tool-brush", "Brush", draft?.currentTool === "brush", draft !== null, () => {
          selectTool("brush");
        }, "B"),
        radioMenuItem("tool-fill", "Fill", draft?.currentTool === "fill", draft !== null, () => {
          selectTool("fill");
        }, "F"),
        radioMenuItem("tool-line", "Line", draft?.currentTool === "line", draft !== null, () => {
          selectTool("line");
        }, "L"),
        radioMenuItem("tool-rectangle", "Rectangle", draft?.currentTool === "rectangle", draft !== null, () => {
          selectTool("rectangle");
        }, "R")
      ]
    },
    {
      id: "write",
      label: "Write",
      items: [buttonMenuItem("write-sprite-registers", elements.writeTrigger)]
    }
  ];

  const mutateDraft = (
    label: string,
    mutation: (currentDraft: SpriteDraft) => boolean,
    options?: { dirtyScope?: "selected" | "all" | "shared" | "mask" | "selected+mask"; rerenderAllSlots?: boolean }
  ): void => {
    const currentDraft = draftOrThrow();
    const snapshot = cloneDraft(currentDraft);
    const mutated = mutation(currentDraft);
    if (!mutated) {
      return;
    }
    history.pushUndoSnapshot(label, snapshot);
    if (options?.dirtyScope === "all") {
      markAllSlotsDirty(currentDraft);
    } else if (options?.dirtyScope === "shared") {
      currentDraft.sharedDirty = true;
      recomputeDirty(currentDraft);
    } else if (options?.dirtyScope === "mask") {
      currentDraft.spriteMaskDirty = true;
      recomputeDirty(currentDraft);
    } else if (options?.dirtyScope === "selected+mask") {
      currentDraft.spriteMaskDirty = true;
      markSelectedSlotDirty(currentDraft);
    } else {
      markSelectedSlotDirty(currentDraft);
    }
    normalizeCursor(currentDraft);
    pendingShapeAnchor = null;
    syncSession();
    renderDirtyIndicator();
    renderHistoryControls();
    if (options?.rerenderAllSlots) {
      renderSlotList();
    } else {
      renderSlotList([currentDraft.selectedIndex]);
    }
    renderEditor();
    renderColorTargets();
    renderBrushes();
    renderPalette();
    refreshMenuBar();
    setStatus(label);
  };

  const renderSpriteActionButtons = (): void => {
    elements.spriteActionButtons.replaceChildren();
    const actions: Array<{
      label: string;
      mutate: (slot: SpriteSlotState) => void;
    }> = [
      { label: "Clear", mutate: (slot) => slot.bytes.fill(0) },
      { label: "Mirror X", mutate: (slot) => mirrorSlotHorizontal(slot) },
      { label: "Mirror Y", mutate: (slot) => mirrorSlotVertical(slot) },
      { label: "Shift Left", mutate: (slot) => shiftSlot(slot, -1, 0) },
      { label: "Shift Right", mutate: (slot) => shiftSlot(slot, 1, 0) },
      { label: "Shift Up", mutate: (slot) => shiftSlot(slot, 0, -1) },
      { label: "Shift Down", mutate: (slot) => shiftSlot(slot, 0, 1) }
    ];

    for (const action of actions) {
      const button = document.createElement("button");
      button.type = "button";
      button.className = "sprite-tool-button";
      button.textContent = action.label;
      button.disabled = draft === null;
      button.addEventListener("click", () => {
        mutateDraft(`${action.label} Sprite ${draftOrThrow().selectedIndex}.`, (currentDraft) => {
          const slot = currentDraft.slots[currentDraft.selectedIndex];
          if (slot === undefined) {
            return false;
          }
          action.mutate(slot);
          return true;
        });
      });
      elements.spriteActionButtons.append(button);
    }
  };

  const renderSlotActionButtons = (): void => {
    elements.slotActionButtons.replaceChildren();
    const copy = document.createElement("button");
    copy.type = "button";
    copy.className = "sprite-tool-button";
    copy.textContent = "Copy Slot";
    copy.disabled = draft === null;
    copy.addEventListener("click", () => {
      const slot = selectedSlot();
      if (slot === null) {
        return;
      }
      clipboard = {
        bytes: cloneBytes(slot.bytes),
        color: slot.color,
        multicolor: slot.multicolor,
        sourceIndex: slot.index
      };
      renderSlotActionButtons();
      setStatus(`Copied Sprite ${slot.index}. Select another slot and click Paste Slot to clone it there.`);
    });
    elements.slotActionButtons.append(copy);

    const paste = document.createElement("button");
    paste.type = "button";
    paste.className = "sprite-tool-button";
    paste.textContent = clipboard === null ? "Paste Slot" : `Paste Slot (from S${clipboard.sourceIndex})`;
    paste.disabled = draft === null || clipboard === null;
    paste.addEventListener("click", () => {
      const copied = clipboard;
      if (copied === null) {
        return;
      }
      mutateDraft(`Pasted Sprite ${copied.sourceIndex} into Sprite ${draftOrThrow().selectedIndex}.`, (currentDraft) => {
        const slot = currentDraft.slots[currentDraft.selectedIndex];
        if (slot === undefined) {
          return false;
        }
        slot.bytes.set(copied.bytes);
        slot.color = copied.color;
        slot.multicolor = copied.multicolor;
        normalizeCursor(currentDraft);
        return true;
      });
    });
    elements.slotActionButtons.append(paste);

    const clearAll = document.createElement("button");
    clearAll.type = "button";
    clearAll.className = "sprite-tool-button";
    clearAll.textContent = "Clear All Slots";
    clearAll.disabled = draft === null;
    clearAll.addEventListener("click", () => {
      mutateDraft("Cleared all eight local sprite slots.", (currentDraft) => {
        for (const slot of currentDraft.slots) {
          slot.bytes.fill(0);
        }
        return true;
      }, { dirtyScope: "all", rerenderAllSlots: true });
    });
    elements.slotActionButtons.append(clearAll);

    const mirrorAll = document.createElement("button");
    mirrorAll.type = "button";
    mirrorAll.className = "sprite-tool-button";
    mirrorAll.textContent = "Mirror All Slots";
    mirrorAll.disabled = draft === null;
    mirrorAll.addEventListener("click", () => {
      mutateDraft("Mirrored all eight local sprite slots horizontally.", (currentDraft) => {
        for (const slot of currentDraft.slots) {
          mirrorSlotHorizontal(slot);
        }
        return true;
      }, { dirtyScope: "all", rerenderAllSlots: true });
    });
    elements.slotActionButtons.append(mirrorAll);
  };

  const renderAll = (): void => {
    renderDirtyIndicator();
    renderHistoryControls();
    renderSlotList();
    renderEditor();
    renderColorTargets();
    renderBrushes();
    renderPalette();
    renderToolButtons();
    renderSpriteActionButtons();
    renderSlotActionButtons();
    refreshMenuBar();
  };

  const replaceDraft = (nextDraft: SpriteDraft, message: string, options?: { pushUndoLabel?: string }): void => {
    clearTransientToolState();
    if (options?.pushUndoLabel !== undefined && draft !== null) {
      history.pushUndoSnapshot(options.pushUndoLabel, draft);
    } else {
      history.clear();
    }
    draft = nextDraft;
    normalizeCursor(nextDraft);
    syncSession();
    renderAll();
    setStatus(message);
  };

  const selectSlot = (nextIndex: number): void => {
    if (draft === null) {
      return;
    }
    const clamped = Math.max(0, Math.min(SPRITE_COUNT - 1, nextIndex));
    if (clamped === draft.selectedIndex) {
      return;
    }
    const previousIndex = draft.selectedIndex;
    draft.selectedIndex = clamped;
    normalizeCursor(draft);
    syncSession();
    renderSlotList([previousIndex, clamped]);
    renderEditor();
    renderColorTargets();
    renderBrushes();
    renderPalette();
    renderToolButtons();
    renderSpriteActionButtons();
    renderSlotActionButtons();
    refreshMenuBar();
    const slot = selectedSlot();
    if (slot !== null) {
      setStatus(`Selected Sprite ${slot.index} at ${formatHexWordPrefixed(slot.address)}.`);
    }
  };

  const applyPaletteToTarget = (paletteIndex: number): void => {
    const currentDraft = draftOrThrow();
    const slot = currentDraft.slots[currentDraft.selectedIndex];
    if (slot === undefined) {
      return;
    }
    mutateDraft(`${colorLabel(currentDraft.activeColorTarget)} set to palette ${paletteIndex} for Sprite ${slot.index}.`, (mutableDraft) => {
      const mutableSlot = mutableDraft.slots[mutableDraft.selectedIndex]!;
      if (mutableDraft.activeColorTarget === "sprite") {
        mutableSlot.color = paletteIndex;
      } else if (mutableDraft.activeColorTarget === "mc0") {
        mutableDraft.mc0 = paletteIndex;
      } else {
        mutableDraft.mc1 = paletteIndex;
      }
      return true;
    }, {
      dirtyScope: currentDraft.activeColorTarget === "sprite" ? "selected" : "shared",
      rerenderAllSlots: currentDraft.activeColorTarget !== "sprite"
    });
  };

  const setCanvasCursor = (point: PaintPoint): void => {
    const currentDraft = draft;
    const slot = selectedSlot();
    if (currentDraft === null || slot === null) {
      return;
    }
    currentDraft.cursorCol = Math.max(0, Math.min(logicalColumns(slot.multicolor) - 1, point.x));
    currentDraft.cursorRow = Math.max(0, Math.min(SPRITE_HEIGHT - 1, point.y));
    syncSession();
    renderEditor();
  };

  const applyPointsWithBrush = (points: PaintPoint[], brush: BrushId, mode: "set" | "toggle"): boolean => {
    const currentDraft = draftOrThrow();
    const slot = currentDraft.slots[currentDraft.selectedIndex];
    if (slot === undefined) {
      return false;
    }
    let mutated = false;
    for (const point of points) {
      if (point.x < 0 || point.x >= logicalColumns(slot.multicolor) || point.y < 0 || point.y >= SPRITE_HEIGHT) {
        continue;
      }
      mutated = applyBrush(slot, point.y, point.x, brush, mode) || mutated;
    }
    if (mutated) {
      markSelectedSlotDirty(currentDraft);
      syncSession();
      renderDirtyIndicator();
      renderSlotList([currentDraft.selectedIndex]);
      renderEditor();
      renderColorTargets();
      renderBrushes();
      renderPalette();
    }
    return mutated;
  };

  const performShapeTool = (point: PaintPoint): void => {
    const currentDraft = draftOrThrow();
    const slot = currentDraft.slots[currentDraft.selectedIndex];
    if (slot === undefined) {
      return;
    }
    currentDraft.cursorCol = point.x;
    currentDraft.cursorRow = point.y;

    if (currentDraft.currentTool === "fill") {
      mutateDraft(`Filled Sprite ${slot.index} from row ${point.y}, cell ${point.x}.`, (mutableDraft) => {
        const mutableSlot = mutableDraft.slots[mutableDraft.selectedIndex]!;
        const replacementCode = mutableSlot.multicolor ? brushToCode(mutableDraft.activeBrush) : mutableDraft.activeBrush === "transparent" ? 0 : 2;
        const fillPoints = rasterizeFloodFill(
          point,
          logicalColumns(mutableSlot.multicolor),
          SPRITE_HEIGHT,
          (x, y) => readCellCode(mutableSlot, y, x),
          replacementCode
        );
        if (fillPoints.length === 0) {
          return false;
        }
        for (const fillPoint of fillPoints) {
          setCellCode(mutableSlot, fillPoint.y, fillPoint.x, replacementCode);
        }
        mutableDraft.cursorCol = point.x;
        mutableDraft.cursorRow = point.y;
        return true;
      });
      return;
    }

    if (currentDraft.currentTool === "line" || currentDraft.currentTool === "rectangle") {
      if (pendingShapeAnchor === null) {
        pendingShapeAnchor = point;
        setStatus(`Set the ${toolLabel(currentDraft.currentTool).toLowerCase()} start point at row ${point.y}, cell ${point.x}. Click a second point to finish the shape.`);
        renderEditor();
        return;
      }
      const anchor = pendingShapeAnchor;
      mutateDraft(
        `${toolLabel(currentDraft.currentTool)} on Sprite ${slot.index} from row ${anchor.y}, cell ${anchor.x} to row ${point.y}, cell ${point.x}.`,
        (mutableDraft) => {
          const mutableSlot = mutableDraft.slots[mutableDraft.selectedIndex]!;
          const points = mutableDraft.currentTool === "line"
            ? rasterizeLine(anchor, point)
            : rasterizeRectangleOutline(anchor, point);
          let changed = false;
          for (const shapePoint of points) {
            changed = applyBrush(mutableSlot, shapePoint.y, shapePoint.x, mutableDraft.activeBrush, "set") || changed;
          }
          mutableDraft.cursorCol = point.x;
          mutableDraft.cursorRow = point.y;
          return changed;
        }
      );
      return;
    }

    mutateDraft(`Edited Sprite ${slot.index} row ${point.y}, cell ${point.x}.`, (mutableDraft) => {
      const mutableSlot = mutableDraft.slots[mutableDraft.selectedIndex]!;
      mutableDraft.cursorCol = point.x;
      mutableDraft.cursorRow = point.y;
      return applyBrush(mutableSlot, point.y, point.x, mutableDraft.activeBrush, "toggle");
    });
  };

  const beginBrushStroke = (point: PaintPoint, brush: BrushId, toggleFirstCell: boolean): void => {
    const currentDraft = draftOrThrow();
    if (strokeHistorySnapshot === null) {
      strokeHistorySnapshot = cloneDraft(currentDraft);
      strokeMutated = false;
    }
    currentDraft.cursorCol = point.x;
    currentDraft.cursorRow = point.y;
    const mutated = applyPointsWithBrush([point], brush, toggleFirstCell ? "toggle" : "set");
    strokeMutated = strokeMutated || mutated;
    lastPaintedPoint = point;
    if (mutated) {
      setStatus(`Edited Sprite ${currentDraft.selectedIndex} row ${point.y}, cell ${point.x}. Changes stay local until Write Sprite + Registers.`);
    }
  };

  const continueBrushStroke = (point: PaintPoint): void => {
    if (lastPaintedPoint !== null && lastPaintedPoint.x === point.x && lastPaintedPoint.y === point.y) {
      return;
    }
    const currentDraft = draftOrThrow();
    currentDraft.cursorCol = point.x;
    currentDraft.cursorRow = point.y;
    const points = lastPaintedPoint === null ? [point] : rasterizeLine(lastPaintedPoint, point);
    const mutated = applyPointsWithBrush(points, paintingBrush, "set");
    strokeMutated = strokeMutated || mutated;
    lastPaintedPoint = point;
  };

  const loadLiveSprites = (): void => {
    try {
      if (!confirmDiscardDirtyDraft("reading sprites from the emulator")) {
        setStatus("Kept the current sprite draft.");
        return;
      }
      const nextDraft = readDraftFromEmulator(emulator, draft);
      replaceDraft(
        nextDraft,
        `Read ${SPRITE_COUNT} sprite slots from bank ${nextDraft.bankBase === 0 ? 0 : 1}; pointers start at ${formatHexWordPrefixed(nextDraft.spritePtrBase)}.`
      );
    } catch (error) {
      setStatus(error instanceof Error ? error.message : String(error));
    }
  };

  const ensureDraftForLocalEditing = (): SpriteDraft => {
    if (draft !== null) {
      return draft;
    }
    const nextDraft = readDraftFromEmulator(emulator, null);
    draft = nextDraft;
    history.clear();
    syncSession();
    renderAll();
    return nextDraft;
  };

  const writeDraft = (): void => {
    const currentDraft = draftOrThrow();
    const slot = currentDraft.slots[currentDraft.selectedIndex];
    if (slot === undefined) {
      throw new Error("Selected sprite slot is unavailable.");
    }
    emulator.writeMemory(slot.address, slot.bytes);
    emulator.writeMemory(SPRITE_MULTICOLOR_ADDR, Uint8Array.of(multicolorMask(currentDraft.slots)));
    emulator.writeMemory(SPRITE_MC0_ADDR, Uint8Array.of(currentDraft.mc0));
    emulator.writeMemory(SPRITE_MC1_ADDR, Uint8Array.of(currentDraft.mc1));
    emulator.writeMemory(SPRITE_COLOR_BASE_ADDR + slot.index, Uint8Array.of(slot.color));
    currentDraft.dirtySlots[currentDraft.selectedIndex] = false;
    currentDraft.sharedDirty = false;
    currentDraft.spriteMaskDirty = false;
    recomputeDirty(currentDraft);
    history.clear();
    syncSession();
    renderDirtyIndicator();
    renderHistoryControls();
    renderEditor();
    refreshMenuBar();
    setStatus(
      `Wrote Sprite ${slot.index} to ${formatHexWordPrefixed(slot.address)} and updated Sprite Color, MC0, MC1, and SPR_MC_EN for bank ${currentDraft.bankBase === 0 ? 0 : 1}.${currentDraft.dirty ? " Other local sprite edits remain unsaved." : ""}`
    );
  };

  const undoDraft = (): void => {
    if (draft === null) {
      return;
    }
    const label = history.getState().lastUndoLabel ?? "the last sprite change";
    const next = history.undo(draft);
    if (next === null) {
      setStatus("Nothing to undo.");
      return;
    }
    draft = next;
    normalizeCursor(draft);
    pendingShapeAnchor = null;
    syncSession();
    renderAll();
    setStatus(`Undid ${label}.`);
  };

  const redoDraft = (): void => {
    if (draft === null) {
      return;
    }
    const label = history.getState().lastRedoLabel ?? "the last undone sprite change";
    const next = history.redo(draft);
    if (next === null) {
      setStatus("Nothing to redo.");
      return;
    }
    draft = next;
    normalizeCursor(draft);
    pendingShapeAnchor = null;
    syncSession();
    renderAll();
    setStatus(`Redid ${label}.`);
  };

  for (const view of slotViews) {
    view.button.addEventListener("click", () => {
      selectSlot(slotViews.indexOf(view));
    });
  }

  for (const view of colorTargetViews) {
    view.button.addEventListener("click", () => {
      const currentDraft = draftOrThrow();
      currentDraft.activeColorTarget = view.value;
      syncSession();
      renderColorTargets();
      renderPalette();
      renderEditor();
      setStatus(`Palette buttons now edit ${colorLabel(view.value)}.`);
    });
  }

  for (const view of brushViews) {
    view.button.addEventListener("click", () => {
      const currentDraft = draftOrThrow();
      currentDraft.activeBrush = view.value;
      syncSession();
      renderBrushes();
      renderEditor();
      setStatus(`Selected ${brushLabel(view.value)} brush.`);
    });
  }

  for (let index = 0; index < paletteButtons.length; index += 1) {
    paletteButtons[index].addEventListener("click", () => {
      try {
        applyPaletteToTarget(index);
      } catch (error) {
        setStatus(error instanceof Error ? error.message : String(error));
      }
    });
  }

  elements.multicolorToggle.addEventListener("change", () => {
    try {
      mutateDraft(`Sprite ${draftOrThrow().selectedIndex} editor switched to ${elements.multicolorToggle.checked ? "multicolor" : "single-color"} mode.`, (currentDraft) => {
        const slot = currentDraft.slots[currentDraft.selectedIndex];
        if (slot === undefined) {
          return false;
        }
        slot.multicolor = elements.multicolorToggle.checked;
        normalizeCursor(currentDraft);
        return true;
      }, { dirtyScope: "selected+mask" });
    } catch (error) {
      setStatus(error instanceof Error ? error.message : String(error));
    }
  });

  elements.editorCanvas.addEventListener("contextmenu", (event) => {
    event.preventDefault();
  });

  elements.editorCanvas.addEventListener("pointerdown", (event) => {
    try {
      const currentDraft = ensureDraftForLocalEditing();
      const slot = currentDraft.slots[currentDraft.selectedIndex];
      if (slot === undefined) {
        return;
      }
      const point = canvasCellFromEvent(event, elements.editorCanvas, slot);
      if (point === null) {
        return;
      }
      elements.editorCanvas.focus();
      setCanvasCursor(point);
      if (currentDraft.currentTool !== "brush") {
        event.preventDefault();
        performShapeTool(point);
        return;
      }
      event.preventDefault();
      paintingPointerId = event.pointerId;
      paintingBrush = event.button === 2 ? "transparent" : currentDraft.activeBrush;
      elements.editorCanvas.setPointerCapture(event.pointerId);
      beginBrushStroke(point, paintingBrush, true);
    } catch (error) {
      setStatus(error instanceof Error ? error.message : String(error));
    }
  });

  elements.editorCanvas.addEventListener("pointermove", (event) => {
    if (paintingPointerId === null || event.pointerId !== paintingPointerId) {
      return;
    }
    const slot = selectedSlot();
    if (slot === null) {
      return;
    }
    const point = canvasCellFromEvent(event, elements.editorCanvas, slot);
    if (point === null) {
      return;
    }
    continueBrushStroke(point);
  });

  const finishPointerStroke = (event?: PointerEvent): void => {
    if (paintingPointerId === null) {
      return;
    }
    if (event !== undefined) {
      const slot = selectedSlot();
      if (slot !== null) {
        const point = canvasCellFromEvent(event, elements.editorCanvas, slot);
        if (point !== null) {
          continueBrushStroke(point);
        }
      }
    }
    commitStrokeHistory();
    renderAll();
  };

  elements.editorCanvas.addEventListener("pointerup", finishPointerStroke);
  elements.editorCanvas.addEventListener("pointercancel", finishPointerStroke);
  elements.editorCanvas.addEventListener("pointerleave", () => {
    if (paintingPointerId !== null) {
      commitStrokeHistory();
      renderAll();
    }
  });

  elements.readTrigger.addEventListener("click", () => {
    loadLiveSprites();
  });

  elements.newBlankTrigger.addEventListener("click", () => {
    try {
      ensureDraftForLocalEditing();
      mutateDraft(`Cleared Sprite ${draftOrThrow().selectedIndex} in the editor. Click Write Sprite + Registers to store the blank image.`, (currentDraft) => {
        currentDraft.slots[currentDraft.selectedIndex]?.bytes.fill(0);
        return true;
      });
    } catch (error) {
      setStatus(error instanceof Error ? error.message : String(error));
    }
  });

  elements.undoTrigger.addEventListener("click", () => {
    undoDraft();
  });

  elements.redoTrigger.addEventListener("click", () => {
    redoDraft();
  });

  elements.writeTrigger.addEventListener("click", () => {
    try {
      writeDraft();
    } catch (error) {
      setStatus(error instanceof Error ? error.message : String(error));
    }
  });

  elements.exportSpriteTrigger.addEventListener("click", () => {
    try {
      const slot = selectedSlot();
      if (slot === null) {
        throw new Error("Load or create a sprite draft first.");
      }
      downloadBinary(slot.bytes, `sprite-s${slot.index}.spr`);
      setStatus(`Exported Sprite ${slot.index} as a 64-byte sprite binary.`);
    } catch (error) {
      setStatus(error instanceof Error ? error.message : String(error));
    }
  });

  elements.importSpriteTrigger.addEventListener("click", () => {
    elements.importSpriteInput.click();
  });

  elements.importSpriteInput.addEventListener("change", async () => {
    const [file] = Array.from(elements.importSpriteInput.files ?? []);
    elements.importSpriteInput.value = "";
    if (file === undefined) {
      return;
    }
    try {
      ensureDraftForLocalEditing();
      const bytes = new Uint8Array(await file.arrayBuffer());
      if (bytes.byteLength !== SPRITE_SLOT_BYTES) {
        throw new Error(`Sprite binary imports must be exactly ${SPRITE_SLOT_BYTES} bytes.`);
      }
      mutateDraft(`Imported ${file.name || "sprite binary"} into Sprite ${draftOrThrow().selectedIndex}.`, (currentDraft) => {
        currentDraft.slots[currentDraft.selectedIndex]?.bytes.set(bytes);
        return true;
      });
    } catch (error) {
      setStatus(error instanceof Error ? error.message : String(error));
    }
  });

  elements.exportProjectTrigger.addEventListener("click", () => {
    try {
      const currentDraft = draftOrThrow();
      downloadText("sprite-project.json", JSON.stringify(createProjectData(currentDraft), null, 2));
      setStatus("Exported the full sprite editor project as JSON.");
    } catch (error) {
      setStatus(error instanceof Error ? error.message : String(error));
    }
  });

  elements.importProjectTrigger.addEventListener("click", () => {
    elements.importProjectInput.click();
  });

  elements.importProjectInput.addEventListener("change", async () => {
    const [file] = Array.from(elements.importProjectInput.files ?? []);
    elements.importProjectInput.value = "";
    if (file === undefined) {
      return;
    }
    try {
      if (!confirmDiscardDirtyDraft(`importing ${file.name}`)) {
        setStatus("Kept the current sprite draft.");
        return;
      }
      const nextDraft = parseProjectData(await file.text());
      nextDraft.dirtySlots = Array.from({ length: SPRITE_COUNT }, () => true);
      nextDraft.sharedDirty = true;
      nextDraft.spriteMaskDirty = true;
      recomputeDirty(nextDraft);
      replaceDraft(nextDraft, `Imported sprite project from ${file.name || "the selected file"}.`, draft === null ? undefined : { pushUndoLabel: "Import sprite project" });
    } catch (error) {
      setStatus(error instanceof Error ? error.message : String(error));
    }
  });

  window.addEventListener("keydown", (event) => {
    if (elements.panel.hidden || isEditableElement(event.target)) {
      return;
    }
    const modifier = event.ctrlKey || event.metaKey;
    if (modifier && event.key.toLowerCase() === "z") {
      event.preventDefault();
      if (event.shiftKey) {
        redoDraft();
        return;
      }
      undoDraft();
      return;
    }
    if (modifier && event.key.toLowerCase() === "y") {
      event.preventDefault();
      redoDraft();
      return;
    }
    if (draft === null) {
      return;
    }
    if (event.key === "Escape" && pendingShapeAnchor !== null) {
      pendingShapeAnchor = null;
      renderEditor();
      setStatus("Cancelled the pending shape anchor.");
      return;
    }

    if (/^[1-8]$/.test(event.key)) {
      event.preventDefault();
      selectSlot(Number.parseInt(event.key, 10) - 1);
      return;
    }

    if (event.key.toLowerCase() === "b") {
      event.preventDefault();
      selectTool("brush");
      return;
    }
    if (event.key.toLowerCase() === "f") {
      event.preventDefault();
      selectTool("fill");
      return;
    }
    if (event.key.toLowerCase() === "l") {
      event.preventDefault();
      selectTool("line");
      return;
    }
    if (event.key.toLowerCase() === "r") {
      event.preventDefault();
      selectTool("rectangle");
      return;
    }
    if (event.key.toLowerCase() === "m") {
      event.preventDefault();
      elements.multicolorToggle.checked = !elements.multicolorToggle.checked;
      elements.multicolorToggle.dispatchEvent(new Event("change"));
      return;
    }
    if (event.key.toLowerCase() === "h") {
      event.preventDefault();
      mutateDraft(`Mirror X on Sprite ${draft.selectedIndex}.`, (currentDraft) => {
        const slot = currentDraft.slots[currentDraft.selectedIndex];
        if (slot === undefined) {
          return false;
        }
        mirrorSlotHorizontal(slot);
        return true;
      });
      return;
    }
    if (event.key.toLowerCase() === "v") {
      event.preventDefault();
      mutateDraft(`Mirror Y on Sprite ${draft.selectedIndex}.`, (currentDraft) => {
        const slot = currentDraft.slots[currentDraft.selectedIndex];
        if (slot === undefined) {
          return false;
        }
        mirrorSlotVertical(slot);
        return true;
      });
      return;
    }
    if (event.altKey && event.key === "ArrowLeft") {
      event.preventDefault();
      mutateDraft(`Shift Left on Sprite ${draft.selectedIndex}.`, (currentDraft) => {
        const slot = currentDraft.slots[currentDraft.selectedIndex];
        if (slot === undefined) {
          return false;
        }
        shiftSlot(slot, -1, 0);
        return true;
      });
      return;
    }
    if (event.altKey && event.key === "ArrowRight") {
      event.preventDefault();
      mutateDraft(`Shift Right on Sprite ${draft.selectedIndex}.`, (currentDraft) => {
        const slot = currentDraft.slots[currentDraft.selectedIndex];
        if (slot === undefined) {
          return false;
        }
        shiftSlot(slot, 1, 0);
        return true;
      });
      return;
    }
    if (event.altKey && event.key === "ArrowUp") {
      event.preventDefault();
      mutateDraft(`Shift Up on Sprite ${draft.selectedIndex}.`, (currentDraft) => {
        const slot = currentDraft.slots[currentDraft.selectedIndex];
        if (slot === undefined) {
          return false;
        }
        shiftSlot(slot, 0, -1);
        return true;
      });
      return;
    }
    if (event.altKey && event.key === "ArrowDown") {
      event.preventDefault();
      mutateDraft(`Shift Down on Sprite ${draft.selectedIndex}.`, (currentDraft) => {
        const slot = currentDraft.slots[currentDraft.selectedIndex];
        if (slot === undefined) {
          return false;
        }
        shiftSlot(slot, 0, 1);
        return true;
      });
      return;
    }

    const slot = selectedSlot();
    if (slot === null) {
      return;
    }
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      draft.cursorCol = Math.max(0, draft.cursorCol - 1);
      syncSession();
      renderEditor();
      return;
    }
    if (event.key === "ArrowRight") {
      event.preventDefault();
      draft.cursorCol = Math.min(logicalColumns(slot.multicolor) - 1, draft.cursorCol + 1);
      syncSession();
      renderEditor();
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      draft.cursorRow = Math.max(0, draft.cursorRow - 1);
      syncSession();
      renderEditor();
      return;
    }
    if (event.key === "ArrowDown") {
      event.preventDefault();
      draft.cursorRow = Math.min(SPRITE_HEIGHT - 1, draft.cursorRow + 1);
      syncSession();
      renderEditor();
      return;
    }
    if (event.key === " " || event.key === "Enter") {
      event.preventDefault();
      const point = { x: draft.cursorCol, y: draft.cursorRow };
      if (draft.currentTool === "brush") {
        mutateDraft(`Edited Sprite ${draft.selectedIndex} row ${point.y}, cell ${point.x}.`, (currentDraft) => {
          const mutableSlot = currentDraft.slots[currentDraft.selectedIndex];
          if (mutableSlot === undefined) {
            return false;
          }
          currentDraft.cursorCol = point.x;
          currentDraft.cursorRow = point.y;
          return applyBrush(mutableSlot, point.y, point.x, currentDraft.activeBrush, "toggle");
        });
        return;
      }
      performShapeTool(point);
    }
  });

  menuBar = createWorkbenchMenuBar(menuHost, {
    getMenus: buildMenus,
    label: "Sprite Editor menu bar"
  });

  if (draft !== null) {
    normalizeCursor(draft);
    setStatus("Restored a local sprite draft from this browser session. Read Sprites to discard it and reload the live machine state.");
  } else {
    setStatus("Open the Sprites tab, click Read Sprites, or import a sprite project to inspect and edit the current sprite slots.");
  }
  renderAll();

  const controller: SpriteEditorController = {
    refresh() {
      loadLiveSprites();
    }
  };

  return controller;
}
